import { brl } from "@/lib/api";

/**
 * A mesma etiqueta de produto, em ZPL — a linguagem da Zebra.
 *
 * ── Por que existe um segundo caminho ──────────────────────────────────────
 * A etiqueta em HTML ([label.js]) é desenhada como página e vai pelo driver.
 * Funciona no mac e em etiquetadora genérica. No WINDOWS com Zebra, não: o
 * driver da Zebra imprime no tamanho de etiqueta configurado NELE e ignora (ou
 * arredonda) o `pageSize` que o Chrome pede. O resultado é adesivo deslocado do
 * picote, escalado, ou em branco — e nada disso dá erro, o que é pior.
 *
 * Em ZPL quem manda na medida é o comando (`^PW`, `^LL`), e a impressora
 * obedece sem driver no meio. É por isso que este arquivo existe.
 *
 * ── O que NÃO fazemos aqui ─────────────────────────────────────────────────
 * Não geramos imagem. O código de barras vai como comando `^BC`, desenhado pelo
 * firmware da Zebra: sai mais nítido que qualquer bitmap que a gente mandasse, e
 * é o firmware que garante as proporções que o leitor espera.
 */

/** 203dpi é o padrão das Zebra de balcão (GC420t, ZD220, ZD230). 8 pontos/mm. */
const DPMM = 8;

const mmParaPontos = (mm) => Math.round(Number(mm) * DPMM);

/**
 * Sanitiza texto para ZPL.
 *
 * `^`, `~` e `\\` são os caracteres de controle da linguagem: um nome de produto
 * com "~" viraria comando no meio da etiqueta. Acento vira sua letra base —
 * a Zebra sem fonte estendida imprime caixa vazia no lugar, e "Munhequeira"
 * legível é melhor que "Munhequeira" com quadrado no meio.
 */
function limpar(texto) {
  return String(texto ?? "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[\^~\\]/g, " ")
    .trim();
}

/** Corta pelo comprimento, com reticências — nome longo empurra o resto para fora. */
function encurtar(texto, max) {
  const t = limpar(texto);
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}.`;
}

/**
 * Uma etiqueta em ZPL.
 *
 * @param {object} etiqueta { name, variant_label, price, barcode }
 * @param {object} papel    { width_mm, height_mm } — do perfil do agente
 * @param {number} copias   `^PQ`: a própria impressora repete, sem ida e volta
 */
export function buildLabelZpl(etiqueta, { width_mm: largura = 60, height_mm: altura = 40 } = {}, copias = 1) {
  const larguraPt = mmParaPontos(largura);
  const alturaPt = mmParaPontos(altura);
  const margem = mmParaPontos(2);
  const util = larguraPt - margem * 2;

  const codigo = limpar(etiqueta.barcode).replace(/\s/g, "");
  // Etiqueta baixa não comporta nome + variação + preço + barras. O nome é o que
  // sai: é o único dado que a pessoa deduz olhando o produto na mão.
  const compacta = altura <= 30;

  // Alturas em pontos, de cima para baixo. Somadas cabem no adesivo com folga —
  // a conta é fixa de propósito: layout que se ajusta sozinho em ZPL vira
  // tentativa e erro na bancada, com adesivo gasto a cada teste.
  const hNome = compacta ? 0 : 22;
  const hVar = 18;
  const hPreco = compacta ? 34 : 42;
  const hBarras = compacta ? mmParaPontos(9) : mmParaPontos(12);

  const temVariacao = etiqueta.variant_label && etiqueta.variant_label !== "Único";
  const linhas = [];
  let y = margem;

  if (!compacta) {
    // `^FB` quebra em duas linhas centralizadas; `^A0N` é a fonte escalável.
    linhas.push(`^FO${margem},${y}^A0N,${hNome},${hNome}^FB${util},2,0,C,0^FD${encurtar(etiqueta.name, 42)}^FS`);
    y += hNome * 2 + 4;
  }
  if (temVariacao) {
    linhas.push(`^FO${margem},${y}^A0N,${hVar},${hVar}^FB${util},1,0,C,0^FD${encurtar(etiqueta.variant_label, 24)}^FS`);
    y += hVar + 4;
  }

  linhas.push(`^FO${margem},${y}^A0N,${hPreco},${hPreco}^FB${util},1,0,C,0^FD${limpar(brl(etiqueta.price))}^FS`);
  y += hPreco + 6;

  if (codigo) {
    /* `^BY2` é a largura do módulo (barra fina) em pontos: 2 pontos a 203dpi
       são 0,25mm, o piso abaixo do qual a térmica borra e o leitor perde o
       código. `^BCN,altura,Y,N,N` = Code128, horizontal, COM o número impresso
       embaixo (o Y) — é ele que salva a conferência quando o adesivo amassa e o
       leitor desiste. */
    linhas.push(`^BY2,3,${hBarras}`);
    linhas.push(`^FO${margem},${y}^FB${util},1,0,C,0^BCN,${hBarras},Y,N,N^FD${codigo}^FS`);
  } else {
    linhas.push(`^FO${margem},${y}^A0N,18,18^FB${util},1,0,C,0^FDSem codigo de barras^FS`);
  }

  return [
    "^XA",
    // `^CI28` liga UTF-8 na impressora. Sem isso, mesmo o texto já sem acento
    // pode sair torto em firmware antigo.
    "^CI28",
    `^PW${larguraPt}`,     // largura de impressão
    `^LL${alturaPt}`,      // comprimento da etiqueta
    "^LH0,0",              // origem no canto, sem deslocamento herdado
    "^MNY",                // sensor de picote ligado: a Zebra acha o vão do adesivo
    ...linhas,
    `^PQ${Math.max(1, Math.min(99, Math.round(Number(copias) || 1)))}`,
    "^XZ",
  ].join("\n");
}
